import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { RootState } from "..";
import authModule from "../../../../utilities/AuthModule";
import { logger } from "../../../../utilities/logger";
import { sameAccountId } from "../../../../utilities/sameAccountId";
import { SharePointSiteHelper } from "../../../../utilities/SharePointSiteHelper";
import { ITeam } from "../../interfaces/services/ITeam";
import { ITeamChannelDto } from "../../interfaces/services/ITeamChannelDto";
import { loadJoinedTeams, getTeamChannels } from "../../services/GraphService";
import { typedAction } from "../helpers";
import { ITeamState } from "../models/reduxModels";
import { RESET_TEAMS, SET_IS_TEAMS_LOADING, SET_TEAMS, SET_TEAM_CHANNELS, SET_TEAMS_LAST_SERVER_REFRESH, SET_TEAMS_LAST_CACHE_REFRESH } from "./actionTypes";
import { decrementAmountOfLoaders, incrementAmountOfLoaders } from "./loader";
import { getLocalStorage, localStorageKeys, setLocalStorage } from "../../components/helpers/localStorageHelper";

const initialState: ITeamState = { isTeamsLoading: false, teams: null, channels: null };

const setIsTeamsLoading = (isTeamsLoading: boolean) => {
    return typedAction(SET_IS_TEAMS_LOADING, isTeamsLoading);
}

const setTeamsLastServerRefresh = () => {
    return typedAction(SET_TEAMS_LAST_SERVER_REFRESH);
}

const setTeamsLastCacheRefresh = () => {
    return typedAction(SET_TEAMS_LAST_CACHE_REFRESH);
}

export const setTeams = (teams: ITeam[]) => {
    return typedAction(SET_TEAMS, teams);
}

export const resetTeams = () => {
    return typedAction(RESET_TEAMS);
}

export const addTeamChannels = (channels: ITeamChannelDto[], teamGroupId: string) => {
    return typedAction(SET_TEAM_CHANNELS, { channels, teamGroupId });
}

// The teams the tenant's allowed-teams setting lets through (all of them when it is empty).
const filterAllowedTeams = (teams: ITeam[], allowedTeams: string[]): ITeam[] =>
    allowedTeams?.length > 0
        ? teams.filter(team => allowedTeams.some(allowedTeam => team.displayName?.trim().toLowerCase() === allowedTeam.trim()))
        : teams;

export const getTeams = (skipLoaders: boolean = false, useCachedTeams: boolean = false): ThunkAction<Promise<ITeam[]>, RootState, unknown, Action<string>> => async (dispatch, getState) => {
    // 2026-10-29 (audit C): the sign-in this load reads for (see the check after loadJoinedTeams)
    const resetsAtStart: number = SharePointSiteHelper.resets;
    const signedInAs: string = (await authModule.getAccount(false))?.accountId;

    if (useCachedTeams) {
        const cachedTeams = getLocalStorage(localStorageKeys.teams);

        if (Array.isArray(cachedTeams) && cachedTeams?.length > 0) {
            // 2026-10-29 (audit B): an outage keeps the saved copy, so one saved before an admin narrowed the setting can outlive it
            dispatch(setTeams(filterAllowedTeams(cachedTeams, getState().tenantInfo.allowedTeams).sort((teamA, teamB) => teamA.displayName?.toUpperCase() < teamB.displayName?.toUpperCase() ? -1 : 1)));
            dispatch(setIsTeamsLoading(false));
            dispatch(decrementAmountOfLoaders());
        }

        dispatch(setTeamsLastCacheRefresh());
    }

    if (!getState().teams.isTeamsLoading) {
        if (!skipLoaders) {
            dispatch(incrementAmountOfLoaders());
            dispatch(setIsTeamsLoading(true));
        }
        let teams: ITeam[];
        try {
            const load = await loadJoinedTeams();
            teams = load.teams;

            // 2026-10-29 (audit C): a load that outlived its sign-in (a sign-out, even when the same account signed in again,
            // or another account's sign-in) writes nothing: the next sign-in loads its own list. Before, an empty list read
            // after a sign-out stayed until a refresh. The hosts' sign-out reset counts the sign-outs (SharePointSiteHelper).
            if (SharePointSiteHelper.resets !== resetsAtStart || !sameAccountId(signedInAs, (await authModule.getAccount(false))?.accountId)) {
                if (!skipLoaders) {
                    dispatch(setIsTeamsLoading(false));
                    dispatch(decrementAmountOfLoaders());
                }
                return getState().teams.teams ?? [];
            }

            // 2026-10-27: the team list could not be read for an outage reason (decided by joinedTeams since 2026-10-28);
            // keep the list (and its cache) instead of writing an empty one
            if (load.outageFailure) {
                // 2026-10-28: with the allowed-teams setting applied as on an answered load (an admin may have narrowed it)
                teams = filterAllowedTeams(getState().teams.teams ?? [], getState().tenantInfo.allowedTeams);
                dispatch(setTeams([...teams].sort((teamA, teamB) => teamA.displayName?.toUpperCase() < teamB.displayName?.toUpperCase() ? -1 : 1)));
                // the load still finishes (consumers wait for the timestamp); only the cache write is skipped
                dispatch(setTeamsLastServerRefresh());
                if (!skipLoaders) dispatch(decrementAmountOfLoaders());
                return teams;
            }

            if (teams?.length > 0) {
                teams = filterAllowedTeams(teams, getState().tenantInfo.allowedTeams);
            }
            setLocalStorage(localStorageKeys.teams, teams);
        } catch (error) {
            logger(error, "teams.ts (getTeams)");
        }
        dispatch(setTeams(teams.sort((teamA, teamB) => teamA.displayName?.toUpperCase() < teamB.displayName?.toUpperCase() ? -1 : 1)));
        dispatch(setTeamsLastServerRefresh());
        if (!skipLoaders) dispatch(decrementAmountOfLoaders());
        return teams;
    }
    return getState().teams.teams ?? [];
};

export const getChannels = (teamGroupId: string, teamDriveId: string): ThunkAction<Promise<ITeamChannelDto[]>, RootState, unknown, Action<string>> => async dispatch => {
    dispatch(incrementAmountOfLoaders());
    let channels: ITeamChannelDto[] = [];
    try {
        const accessToken = await authModule.getGraphAccessToken();
        channels = await getTeamChannels(accessToken, teamGroupId, teamDriveId);
        dispatch(addTeamChannels(channels, teamGroupId));
    } catch (error) {
        logger(error, "teams.ts (getChannels)");
    }
    dispatch(decrementAmountOfLoaders());
    return channels;
};

type TeamAction = ReturnType<typeof setIsTeamsLoading | typeof setTeamsLastServerRefresh | typeof setTeamsLastCacheRefresh | typeof setTeams | typeof addTeamChannels | typeof resetTeams>;

export function teamsReducer(
    state = initialState,
    action: TeamAction
): ITeamState {
    switch (action.type) {
        case SET_IS_TEAMS_LOADING:
            return { ...state, isTeamsLoading: action.payload };
        case SET_TEAMS_LAST_SERVER_REFRESH:
            return { ...state, lastServerFetch: new Date().getTime() };
        case SET_TEAMS_LAST_CACHE_REFRESH:
            return { ...state, lastCacheFetch: new Date().getTime() };
        case SET_TEAMS:
            return { ...state, teams: action.payload, isTeamsLoading: false };
        case RESET_TEAMS:
            return initialState;
        case SET_TEAM_CHANNELS:
            if (state.channels) {
                const filteredChannels: ITeamChannelDto[] = state.channels.filter(channel => channel.teamGroupId !== action.payload.teamGroupId);
                const combinedChannels: ITeamChannelDto[] = filteredChannels
                    .concat(action.payload.channels);

                return {
                    ...state,
                    channels: [
                        ...combinedChannels.filter(x => x.isPrimary),
                        ...combinedChannels
                            .filter(x => !x.isPrimary)
                            .sort((channelA, channelB) => channelA.displayName?.toUpperCase() < channelB.displayName?.toUpperCase() ? -1 : 1)
                    ]
                };
            }
            else {
                return { ...state, channels: action.payload.channels };
            }
        default:
            return state;
    }
}